import {
  Badge,
  Button,
  Calendar,
  type CalendarProps,
  ConfigProvider,
  DatePicker,
  Flex,
  List,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  type TableProps,
  theme,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  forwardRef,
  type ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { OverflowComponent } from '../../../../components/overflow.component';
import SyncSuspense from '../../../../components/sync-suspense';
import { AuthContext } from '../../../../contexts/auth-context-provider';
import { type ScheduledMeal, type Customer } from '../../../../entities';
import { useQuery } from '../../../../jsonapi-provider';
import { Roles } from '../../../../types';
import { LocalDate } from '../../../../utils/local-date';

const NEW_ORDER_PAGE_URL = '/new-order';
interface DataType {
  onConfirm: () => void;
  onCancel: () => void;
  customer: Customer;
  orderDate?: LocalDate | undefined | null;
}
const serializeScheduledMealFilters = (siteId: string, orderDate: LocalDate): string => {
  const parts: string[] = [];
  parts.push('cycleMenu.isEnabled==true');
  if (orderDate) {
    parts.push(
      `day=ge=${orderDate.startOf('month').toISOString()};day=le=${orderDate
        .endOf('month')
        .toISOString()}`
    );
  }

  parts.push(`cycleMenu.sites.id=hasmember=${siteId}`);

  return parts.join(';');
};

const ScheduledMealsCalendarModal = forwardRef((props: DataType, ref) => {
  const { token } = theme.useToken();
  const { session } = useContext(AuthContext);
  const { onConfirm, onCancel, customer, orderDate = new LocalDate(dayjs()) } = props;
  const [selectedDate, setSelectedDate] = useState(orderDate);
  const [showMealsModal, setShowMealsModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const availableMeals = useQuery<ScheduledMeal[]>(
    selectedDate &&
      customer.id &&
      isOpen &&
      selectedSite && [
        'scheduled-meal',
        {
          filter: serializeScheduledMealFilters(selectedSite, selectedDate),
          include: ['mealCategory'],
        },
      ]
  );
  useEffect(() => {
    setSelectedSite(null);
  }, [customer.id]);
  useImperativeHandle(
    ref,
    () => ({
      showModal: () => {
        setIsOpen(true);
      },
      hideModal: () => {
        setIsOpen(false);
      },
    }),
    []
  );
  const dateCellRender = (value: Dayjs): ReactNode => {
    const listData = (availableMeals.data ?? [])
      .filter((ms: any) => ms.day.isSame(value, 'day') && selectedDate?.isSame(value, 'month'))
      .toSorted((a, b) => (a.mealCategory?.sortOrder ?? 0) - (b.mealCategory?.sortOrder ?? 0));

    return (
      <ConfigProvider renderEmpty={() => 'No Meals'}>
        <SyncSuspense
          fallback={
            <>
              <Space direction="vertical">
                <Space>
                  <Skeleton.Avatar active size={'small'} shape={'circle'} />
                  <Skeleton.Button active size="small" shape={'round'} />
                </Space>
                <Space>
                  <Skeleton.Avatar active size={'small'} shape={'circle'} />
                  <Skeleton.Input active size="small" block={true} />
                </Space>
              </Space>
            </>
          }
          condition={!availableMeals.isLoading}
        >
          <List
            dataSource={listData?.slice(0, 2)}
            split={false}
            rowKey="id"
            renderItem={(item: any) => (
              <List.Item style={{ margin: 0, padding: 0 }}>
                <Typography.Text ellipsis>
                  {item.name.slice(0, 14)}
                  {item.name.length > 13 && '...'}
                </Typography.Text>
              </List.Item>
            )}
          />
          {listData.length > 2 && (
            <Typography.Link italic underline style={{ marginLeft: 10 }}>
              +{listData.length - 2} more
            </Typography.Link>
          )}
        </SyncSuspense>
      </ConfigProvider>
    );
  };
  const scheduledMealColumns: TableProps['columns'] = [
    {
      title: <Typography.Text>Site</Typography.Text>,
      render: (_) => (
        <Typography.Text>
          {customer?.sites.find((s) => s.id === selectedSite)?.name}
        </Typography.Text>
      ),
      onCell: (record) => {
        return {
          rowSpan: record.sameSiteFollowingCount,
        };
      },
    },
    {
      dataIndex: ['mealCategory', 'name'],
      title: <Typography.Text>Meal Category</Typography.Text>,
    },
    {
      dataIndex: ['name'],
      title: <Typography.Text>Meal</Typography.Text>,
    },
    {
      dataIndex: ['mealItemsNames'],
      title: <Typography.Text>Meal Items</Typography.Text>,
      render: (names: string[]) => (
        <OverflowComponent
          elementsList={names.map((name, index) => (
            <Badge key={index} color={token.colorPrimary} text={name} />
          ))}
          maxCount={2}
        />
      ),
    },
  ];
  const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
    if (info.type === 'date') return dateCellRender(current);
    return info.originNode;
  };
  const onPanelChange = (value: Dayjs): void => {
    setSelectedDate(new LocalDate(value));
  };
  return (
    <>
      <Modal
        title={
          <Typography.Title
            style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }}
            level={3}
          >
            Scheduled Meals
          </Typography.Title>
        }
        open={isOpen}
        onOk={onConfirm}
        onCancel={onCancel}
        width={1300}
        forceRender
      >
        <Calendar
          defaultValue={selectedDate?.toDayjs()}
          onSelect={(value) => {
            setShowMealsModal(true);
            setSelectedDate(new LocalDate(value));
          }}
          value={selectedDate?.toDayjs()}
          headerRender={() => (
            <Flex justify="space-between">
              <Space>
                <Typography.Text>Select Site</Typography.Text>
                <Select
                  showSearch
                  placeholder="select Site"
                  optionFilterProp="label"
                  defaultValue={null}
                  style={{ minWidth: 300 }}
                  value={selectedSite}
                  onChange={(value) => {
                    setSelectedSite(value);
                  }}
                  options={
                    customer !== undefined
                      ? customer.sites
                          .filter((site) =>
                            session?.claims.role === Roles.ROLE_HHC_ADMIN ? true : site.isActive
                          )
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((site) => ({ label: site.name, value: site.id }))
                      : ([] as any)
                  }
                />
              </Space>
              <Space>
                <Typography.Text>Select Month</Typography.Text>
                <DatePicker
                  picker="month"
                  defaultValue={selectedDate?.toDayjs()}
                  value={selectedDate?.toDayjs()}
                  onChange={(date) => {
                    setSelectedDate(date ? new LocalDate(date) : null);
                  }}
                />
              </Space>
            </Flex>
          )}
          mode="month"
          cellRender={cellRender}
          onPanelChange={onPanelChange}
        />
      </Modal>
      <Modal
        title={
          <Flex justify="space-between" style={{ marginTop: 30 }}>
            <Typography.Title
              style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }}
              level={3}
            >
              Meals Scheduled for {selectedDate?.format('LL')}
            </Typography.Title>
            {/* <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowMealsModal(false);
                onSelectDate(MealToOrderState.filters?.orderDate);
              }}
              type="primary"
              disabled={MealToOrderState.filters?.orderDate < dayjs().add(2, 'day')}
            >
              {' '}
              Place Order
            </Button> */}
          </Flex>
        }
        open={showMealsModal}
        onOk={() => {
          setShowMealsModal(false);
        }}
        onCancel={() => {
          setShowMealsModal(false);
        }}
        width={1500}
        zIndex={999999}
        footer={
          <Flex justify="center" style={{ marginTop: 30 }}>
            <Button
              onClick={() => {
                navigate(
                  NEW_ORDER_PAGE_URL +
                    `?preSelectedOrderDate=${selectedDate?.toISOString()}&preSelectedSiteId=${selectedSite}&preSelectedCustomerId=${
                      customer.id
                    }`
                );
              }}
              type="primary"
              style={{ minWidth: 300 }}
              disabled={(selectedDate ?? new LocalDate()).isBefore(new LocalDate().add(2, 'day'))}
            >
              {' '}
              Place Order
            </Button>
          </Flex>
        }
        forceRender
        destroyOnHidden={true}
      >
        <Flex style={{ height: 600 }}>
          <Table
            size="middle"
            scroll={{ y: 10 * 50 }}
            loading={availableMeals.isLoading}
            // rowKey={(row) => row.id ?? '0'}
            dataSource={
              availableMeals.data
                ?.filter((ms: any) => ms.day.isSame(selectedDate, 'day'))
                .toSorted(
                  (a, b) => (a.mealCategory?.sortOrder ?? 0) - (b.mealCategory?.sortOrder ?? 0)
                ) as ScheduledMeal[]
            }
            columns={scheduledMealColumns}
          />
        </Flex>
      </Modal>
    </>
  );
});
ScheduledMealsCalendarModal.displayName = 'ScheduledMealsCalendarModal';
export default ScheduledMealsCalendarModal;
